import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, ArrowLeft, Globe, Wand2, CheckCircle2, ShieldCheck, Video,
  CalendarCheck, X, MapPin, Star, ArrowRight, Image as ImageIcon, Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOADING_STEPS = ['Scanning your website…', 'Reading your bio & specialties…', 'Mapping it to CaraLoom with GPT-5.2…', 'Building your new profile…'];

const CaraLoomFeatures = () => (
  <div className="space-y-2.5" data-testid="migration-feature-list">
    {[
      { icon: ShieldCheck, title: 'Insurance Billing — active', desc: 'We file claims & handle RCM. Your old site couldn’t bill insurance.' },
      { icon: Video, title: 'Telehealth — active', desc: 'One-click HIPAA video visits, no extra software.' },
      { icon: CalendarCheck, title: 'Instant online booking', desc: 'Families book & pay directly from your profile.' },
    ].map((f) => (
      <div key={f.title} className="flex items-start gap-3 p-3 rounded-xl bg-cn-primary/5 border border-cn-primary/15">
        <span className="w-8 h-8 rounded-lg bg-cn-primary/12 text-cn-primary flex items-center justify-center flex-shrink-0">
          <f.icon className="w-4 h-4" />
        </span>
        <div>
          <p className="text-sm font-bold text-cn-text dark:text-white flex items-center gap-1.5">
            {f.title} <CheckCircle2 className="w-3.5 h-3.5 text-cn-success" />
          </p>
          <p className="text-xs text-cn-text-muted">{f.desc}</p>
        </div>
      </div>
    ))}
  </div>
);

export default function PracticeMigration() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [specInput, setSpecInput] = useState('');

  useEffect(() => {
    let t;
    if (loading) { t = setInterval(() => setStep((s) => Math.min(s + 1, LOADING_STEPS.length - 1)), 1800); }
    return () => clearInterval(t);
  }, [loading]);

  const runImport = async () => {
    if (!url.trim()) { toast.error('Enter your website URL first'); return; }
    setLoading(true); setStep(0); setDraft(null);
    try {
      const { data } = await axios.post(`${API}/clinician/migrate/scrape`, { url: url.trim() }, { ...getAuthHeader(), timeout: 100000 });
      setDraft(data.draft);
      toast.success('Imported! Review your new profile below.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not import that site. Try a different page.');
    } finally { setLoading(false); }
  };

  const upd = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const removeChip = (key, i) => upd(key, draft[key].filter((_, idx) => idx !== i));
  const addSpec = () => {
    const v = specInput.trim();
    if (v && !(draft.specialties || []).includes(v)) upd('specialties', [...(draft.specialties || []), v]);
    setSpecInput('');
  };

  const apply = async () => {
    setApplying(true);
    try {
      const fields = {
        headline: draft.headline, bio: draft.bio, specialties: draft.specialties,
        certifications: draft.certifications, languages: draft.languages,
        license_type: draft.license_type, years_of_experience: draft.years_of_experience,
        city: draft.city, state: draft.state, photo_url: draft.photo_url,
      };
      const { data } = await axios.post(`${API}/clinician/migrate/apply`, { fields }, getAuthHeader());
      toast.success(`Imported ${data.applied.length} fields to your CaraLoom profile 🎉`);
      navigate('/clinician/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not apply. Try again.');
    } finally { setApplying(false); }
  };

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="practice-migration-page">
      <header className="cn-glass border-b border-cn-border sticky top-0 z-30">
        <div className="cn-container h-16 flex items-center justify-between">
          <Link to="/clinician/dashboard" className="flex items-center gap-3">
            <CaraLoomLogo />
            <span className="hidden md:inline-flex items-center gap-1.5 text-sm font-display font-bold text-cn-text">
              <Globe className="w-4 h-4 text-cn-primary" /> Practice Migration
            </span>
          </Link>
          <Link to="/clinician/dashboard" className="cn-btn cn-btn-ghost text-sm" data-testid="migration-back-link">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
        </div>
      </header>

      <main className="cn-container py-10 max-w-6xl">
        <div className="cn-eyebrow mb-2">Move in minutes</div>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold mb-2">Bring your practice to CaraLoom.</h1>
        <p className="text-cn-text-muted mb-7 max-w-2xl">
          Paste your existing website and we’ll import your bio, specialties, and photo with AI — then show you your
          upgraded profile with <span className="font-semibold text-cn-text dark:text-white">insurance billing</span> and{' '}
          <span className="font-semibold text-cn-text dark:text-white">telehealth</span> turned on.
        </p>

        {/* URL input */}
        <div className="cn-card p-5 sm:p-6 mb-8">
          <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">Your current website</Label>
          <div className="mt-2 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-cn-text-muted" />
              <Input
                value={url} onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !loading && runImport()}
                placeholder="yourpractice.com  or  https://yourpractice.com/about"
                className="h-12 pl-10 rounded-xl bg-white border-cn-border" data-testid="migration-url-input"
              />
            </div>
            <Button onClick={runImport} disabled={loading} className="cn-btn cn-btn-primary h-12 px-6" data-testid="migration-import-btn">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Wand2 className="w-4 h-4" /> Import my profile</>}
            </Button>
          </div>
          {loading && (
            <div className="mt-4 flex items-center gap-2 text-sm text-cn-primary font-medium" data-testid="migration-loading-step">
              <Sparkles className="w-4 h-4 animate-pulse" /> {LOADING_STEPS[step]}
            </div>
          )}
        </div>

        {draft && (
          <div className="grid lg:grid-cols-2 gap-6" data-testid="migration-result">
            {/* LEFT — editable imported fields */}
            <div className="cn-card p-5 sm:p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-cn-primary" />
                <h2 className="font-display font-semibold text-lg text-cn-text dark:text-white">Review what we imported</h2>
              </div>
              <p className="text-xs text-cn-text-muted -mt-2">From {draft.source_url}. Edit anything before you apply.</p>

              {/* Photo + candidates */}
              <div>
                <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">Profile photo</Label>
                <div className="mt-2 flex items-center gap-3">
                  {draft.photo_url ? (
                    <img src={draft.photo_url} alt="profile" className="w-16 h-16 rounded-2xl object-cover border border-cn-border" data-testid="migration-photo" />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-cn-bone-100 flex items-center justify-center text-cn-text-muted"><ImageIcon className="w-6 h-6" /></div>
                  )}
                  {(draft.image_candidates || []).length > 1 && (
                    <div className="flex gap-1.5 flex-wrap">
                      {draft.image_candidates.slice(0, 6).map((src, i) => (
                        <button key={i} onClick={() => upd('photo_url', src)}
                          className={`w-10 h-10 rounded-lg overflow-hidden border-2 ${draft.photo_url === src ? 'border-cn-primary' : 'border-transparent opacity-70 hover:opacity-100'}`}
                          data-testid={`migration-photo-candidate-${i}`}>
                          <img src={src} alt={`option ${i}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">Headline</Label>
                <Input value={draft.headline || ''} onChange={(e) => upd('headline', e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white border-cn-border" data-testid="migration-headline" />
              </div>

              <div>
                <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">Bio</Label>
                <Textarea value={draft.bio || ''} onChange={(e) => upd('bio', e.target.value)} rows={4} className="mt-1.5 rounded-xl bg-white border-cn-border" data-testid="migration-bio" />
              </div>

              <div>
                <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">Specialties</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(draft.specialties || []).map((s, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-cn-primary/10 text-cn-primary text-sm font-medium px-2.5 py-1 rounded-full" data-testid={`migration-spec-${i}`}>
                      {s}<button onClick={() => removeChip('specialties', i)}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <Input value={specInput} onChange={(e) => setSpecInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSpec())}
                    placeholder="Add a specialty…" className="h-9 rounded-lg bg-white border-cn-border text-sm" data-testid="migration-spec-input" />
                  <Button onClick={addSpec} variant="outline" className="rounded-lg h-9 px-3 text-sm">Add</Button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">License</Label>
                  <Input value={draft.license_type || ''} onChange={(e) => upd('license_type', e.target.value.toUpperCase())} className="mt-1.5 h-11 rounded-xl bg-white border-cn-border" data-testid="migration-license" />
                </div>
                <div>
                  <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">City</Label>
                  <Input value={draft.city || ''} onChange={(e) => upd('city', e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white border-cn-border" data-testid="migration-city" />
                </div>
                <div>
                  <Label className="text-xs font-bold text-cn-text-muted uppercase tracking-wider">State</Label>
                  <Input value={draft.state || ''} onChange={(e) => upd('state', e.target.value.toUpperCase().slice(0, 2))} maxLength={2} className="mt-1.5 h-11 rounded-xl bg-white border-cn-border" data-testid="migration-state" />
                </div>
              </div>

              {confirmApply ? (
                <div className="rounded-xl border border-cn-accent/40 bg-cn-accent/5 p-3" data-testid="migration-apply-confirm">
                  <p className="text-sm text-cn-text dark:text-white font-medium">Replace your current bio, specialties &amp; photo with the imported version?</p>
                  <div className="mt-2.5 flex gap-2">
                    <Button onClick={apply} disabled={applying} className="cn-btn cn-btn-primary flex-1 h-11" data-testid="migration-apply-confirm-btn">
                      {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</> : <>Yes, apply</>}
                    </Button>
                    <Button onClick={() => setConfirmApply(false)} disabled={applying} variant="outline" className="rounded-xl h-11 px-4" data-testid="migration-apply-cancel-btn">Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => setConfirmApply(true)} className="cn-btn cn-btn-primary w-full h-12" data-testid="migration-apply-btn">
                  <Download className="w-4 h-4" /> Apply to my CaraLoom profile
                </Button>
              )}
            </div>

            {/* RIGHT — live CaraLoom profile preview */}
            <div className="space-y-4">
              <div className="cn-card p-0 overflow-hidden" data-testid="migration-preview-card">
                <div className="h-20 bg-gradient-to-br from-cn-primary to-cn-secondary" />
                <div className="px-6 pb-6 -mt-10">
                  {draft.photo_url ? (
                    <img src={draft.photo_url} alt="preview" className="w-20 h-20 rounded-2xl object-cover border-4 border-white dark:border-cn-ink-800 shadow-lg" />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-cn-bone-100 border-4 border-white flex items-center justify-center text-cn-text-muted"><ImageIcon className="w-7 h-7" /></div>
                  )}
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-xl text-cn-text dark:text-white">{draft.full_name || (user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : 'Your name')}</h3>
                    {draft.license_type && <span className="text-xs font-bold bg-cn-primary/10 text-cn-primary px-2 py-0.5 rounded">{draft.license_type}</span>}
                  </div>
                  <p className="text-sm text-cn-text-muted">{draft.headline}</p>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-cn-text-muted">
                    {(draft.city || draft.state) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[draft.city, draft.state].filter(Boolean).join(', ')}</span>}
                    <span className="inline-flex items-center gap-1 text-cn-accent"><Star className="w-3 h-3 fill-current" /> New on CaraLoom</span>
                  </div>
                  <p className="mt-3 text-sm text-cn-text dark:text-cn-bone-200 line-clamp-4">{draft.bio}</p>
                  {(draft.specialties || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {draft.specialties.slice(0, 6).map((s, i) => (
                        <span key={i} className="text-xs bg-cn-bone-100 dark:bg-white/5 text-cn-text dark:text-cn-bone-200 px-2 py-0.5 rounded-full">{s}</span>
                      ))}
                    </div>
                  )}
                  <Button className="cn-btn cn-btn-primary w-full mt-4 pointer-events-none opacity-95">Book a visit <ArrowRight className="w-4 h-4" /></Button>
                </div>
              </div>

              <div className="cn-card p-5">
                <p className="text-sm font-bold text-cn-text dark:text-white mb-3 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-cn-accent" /> What your old site couldn’t do — now live:
                </p>
                <CaraLoomFeatures />
              </div>
            </div>
          </div>
        )}

        {!draft && !loading && (
          <div className="cn-card p-6">
            <p className="text-sm font-bold text-cn-text dark:text-white mb-3">Why migrate?</p>
            <CaraLoomFeatures />
          </div>
        )}
      </main>
    </div>
  );
}
